/**
 * Tijdlijn van de klant (ontwerp 31, rechts boven): alles van de klant,
 * per dag, nieuwste boven. Segment Alles, Contact, Bestellingen, Dossier.
 * Een item klikt open met de inhoud en een knop naar de zaak (op Vandaag,
 * `/?zaak=<id>`) of naar Lightspeed (nieuw tabblad). Bij een bestelling
 * altijd nummer en bedrag.
 *
 * Sessie 73 blok 3: Dossier toont de dossierregels (kanaal "dossier"),
 * Bestellingen ook de retouren (kanaal "retour"), elk met een eigen
 * icoon. "Meer" leest met de cursor `volgende_voor` telkens 100 oudere
 * regels en plakt ze onder de lijst (ontdubbeld op `sleutel`).
 */
import { useInfiniteQuery } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import { v6Api } from "../api";
import { Icoon, type IcoonNaam } from "../Icoon";
import { euro, veiligeLink, wieWoord } from "../Rechts/opmaak";
import { v6Sleutels } from "../sleutels";
import { dagKop, dagSleutel, tijd, datumKort } from "./opmaak";
import { hoortBij, plakPaginas, type Keuze } from "./tijdlijnHulp";
import type { KaartTijdlijnItem } from "./types";

const KEUZES: { id: Keuze; woord: string }[] = [
  { id: "alles", woord: "Alles" },
  { id: "contact", woord: "Contact" },
  { id: "bestelling", woord: "Bestellingen" },
  { id: "dossier", woord: "Dossier" },
];

function icoonVan(item: KaartTijdlijnItem): IcoonNaam {
  switch (item.kanaal) {
    case "mail":
    case "email":
      return "mail";
    case "chat":
      return "chat";
    case "whatsapp":
      return "whatsapp";
    case "telefoon":
    case "voicemail":
      return "telefoon";
    case "retour":
      return "retour";
    case "dossier":
      return "lijstje";
    default:
      if (item.filter === "bestelling") return "voorraad";
      if (item.filter === "dossier") return "notitie";
      return "doc";
  }
}

const PAGINA = 100;

function Item({ item }: { item: KaartTijdlijnItem }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ls = veiligeLink(item.lightspeed_url);
  const door = [wieWoord(item.door_soort), item.door_naam].filter(Boolean).join(", ");
  return (
    <li className={`kl-tl-item${open ? " open" : ""}`} data-filter={item.filter}>
      <button
        type="button"
        className="kl-tl-knop"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span className={`ic ${item.kanaal}`}>
          <Icoon naam={icoonVan(item)} klein />
        </span>
        <span className="tekst">
          <b className="titel">
            {item.titel}
            {item.status ? <span className={`kl-tl-status${item.status === "afgerond" ? "" : " open"}`}>{item.status}</span> : null}
          </b>
          {item.ordernummer || item.bedrag_cents !== null ? (
            <span className="order num">
              {[item.ordernummer, item.bedrag_cents !== null ? euro(item.bedrag_cents) : null]
                .filter(Boolean)
                .join(" · ")}
            </span>
          ) : null}
          {item.omschrijving ? <span className="oms">{item.omschrijving}</span> : null}
        </span>
        <span className="tijd num">{tijd(item.moment)}</span>
      </button>
      {open ? (
        <div className="kl-tl-open">
          {item.regels.length > 0 ? (
            <ol className="kl-tl-regels">
              {item.regels.map((r, i) => (
                <li key={`${r.moment}-${i}`}>
                  <small className="num">{`${datumKort(r.moment)} ${tijd(r.moment)} · ${r.wie}`}</small>
                  <span>{r.tekst}</span>
                </li>
              ))}
            </ol>
          ) : (
            <>
              {item.inhoud ? <p className="inhoud">{item.inhoud}</p> : null}
              {door ? <small>{door}</small> : null}
            </>
          )}
          <div className="knoppen">
            {item.zaak_id ? (
              <button
                type="button"
                className="v6-knop zacht"
                onClick={() => navigate(`/?zaak=${item.zaak_id}`)}
              >
                Open de zaak
              </button>
            ) : null}
            {ls ? (
              <a className="v6-knop zacht" href={ls} target="_blank" rel="noopener noreferrer">
                Lightspeed <Icoon naam="uit" klein />
              </a>
            ) : null}
          </div>
        </div>
      ) : null}
    </li>
  );
}

export function Tijdlijn({
  klantId,
  voornaam,
  sluitKnop,
}: {
  klantId: string;
  voornaam: string | null;
  sluitKnop?: ReactNode;
}) {
  const [keuze, setKeuze] = useState<Keuze>("alles");
  const tijdlijn = useInfiniteQuery({
    queryKey: v6Sleutels.klantKaartTijdlijn(klantId),
    queryFn: ({ pageParam }) => v6Api.klantKaartTijdlijn(klantId, PAGINA, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (laatste) => laatste.volgende_voor ?? undefined,
  });
  const items = plakPaginas(tijdlijn.data?.pages ?? [])
    .filter((i) => hoortBij(i, keuze))
    .sort((a, b) => (a.moment < b.moment ? 1 : a.moment > b.moment ? -1 : 0));

  const dagen: { sleutel: string; kop: string; items: KaartTijdlijnItem[] }[] = [];
  for (const item of items) {
    const sleutel = dagSleutel(item.moment);
    const laatste = dagen[dagen.length - 1];
    if (laatste && laatste.sleutel === sleutel) laatste.items.push(item);
    else dagen.push({ sleutel, kop: dagKop(item.moment), items: [item] });
  }

  return (
    <>
      <div className="kl-vk">
        <b>{voornaam ? `Alles van ${voornaam}` : "Alles van de klant"}</b>
        {sluitKnop}
      </div>
      <div className="kl-segment" role="group" aria-label="Filter">
        {KEUZES.map((k) => (
          <button
            key={k.id}
            type="button"
            className={keuze === k.id ? "on" : undefined}
            aria-pressed={keuze === k.id}
            onClick={() => setKeuze(k.id)}
          >
            {k.woord}
          </button>
        ))}
      </div>
      <div className="kl-tl-scrol">
        {tijdlijn.isLoading ? (
          <p className="v6-leeg">Tijdlijn wordt geladen.</p>
        ) : tijdlijn.isError ? (
          <p className="v6-fout kl-marge" role="alert">
            De tijdlijn kon niet geladen worden.
          </p>
        ) : dagen.length === 0 ? (
          <p className="v6-leeg">Niets gevonden.</p>
        ) : (
          dagen.map((dag) => (
            <section key={dag.sleutel} className="kl-tl-dag" aria-label={dag.kop}>
              <h3 className="kl-dagkop">{dag.kop}</h3>
              <ul>
                {dag.items.map((item) => (
                  <Item key={item.sleutel} item={item} />
                ))}
              </ul>
            </section>
          ))
        )}
        {tijdlijn.hasNextPage ? (
          <button
            type="button"
            className="v6-knop kl-meer"
            disabled={tijdlijn.isFetchingNextPage}
            onClick={() => void tijdlijn.fetchNextPage()}
          >
            Meer
          </button>
        ) : null}
      </div>
    </>
  );
}
